import React, { useState, useEffect, useRef, useMemo } from 'react';
import apiClient from '../utils/apiClient';
import server from '../utils/serverConfig';
import toast from 'react-hot-toast';
import { 
    UploadCloud, FileCode, Play, Download, AlertCircle, CheckCircle, Loader2, 
    X, RefreshCw, Trash2, Zap, Shield, FileSpreadsheet, Layers, Clock, Activity,
    Cpu, Sparkles, CheckCheck, Radio, Calendar, ArrowRight, RefreshCcw
} from 'lucide-react';
import { useBacktestStore } from '../contexts/LTPStore/useBacktestStore';

export const BacktestView = () => {
    // Mode state: 'optimus' (Index Options) | 'pledge_plus' (MCX Futures)
    const [strategyMode, setStrategyMode] = useState('optimus');
    const [historyFilter, setHistoryFilter] = useState('all'); // 'all' | 'optimus' | 'pledge_plus'
    const [inputMethod, setInputMethod] = useState('auto_sync'); // 'auto_sync' | 'manual_upload'

    // Local state for file input (transient)
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    
    // Automated trigger loading states
    const [isTriggeringOptimus, setIsTriggeringOptimus] = useState(false);
    const [isTriggeringPledge, setIsTriggeringPledge] = useState(false);
    const [pipelineStatus, setPipelineStatus] = useState(null);

    // Global Store State
    const {
        jobId,
        status,
        progress,
        stage,
        error,
        lastUpdated,

        setJobId,
        setStatus,
        setProgress,
        setStage,
        setError,
        setLastUpdated,

        resetState,
        updateState
    } = useBacktestStore();

    const [jobs, setJobs] = useState([]);
    const [jobToDelete, setJobToDelete] = useState(null); // State for delete confirmation modal

    const BACKTEST_API_URL = "https://backtest.sypto.xyz";

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
        return d.toLocaleTimeString();
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
        return d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
    };

    // Helper to detect if a job is Pledge+ (MCX) or Optimus (Index)
    const detectJobCategory = (job) => {
        if (job.job_type === 'mcx_backtest' || job.job_type === 'mcx_audit') return 'pledge_plus';
        const fn = (job.original_filename || '').toUpperCase();
        const mcxTokens = ['GOLDM', 'SILVERM', 'CRUDEOIL', 'CRUDEOILM', 'NATURALGAS', 'NATGASMINI', 'NATGAS', 'COPPER'];
        if (mcxTokens.some(t => fn.includes(t)) || fn.endsWith('.CSV')) return 'pledge_plus';
        return 'optimus';
    };

    const fetchPipelineStatus = async () => {
        try {
            const res = await apiClient.get(`${BACKTEST_API_URL}/api/v1/pipeline/status`);
            setPipelineStatus(res.data);
        } catch (err) {
            console.error("Failed to fetch pipeline status", err);
        }
    };

    const handleTriggerPipeline = async (type) => {
        const isOptimus = type === 'optimus';
        const endpoint = isOptimus ? '/api/v1/pipeline/sync-equity' : '/api/v1/pipeline/sync-mcx';
        const title = isOptimus ? 'Optimus (Index Options)' : 'Pledge+ (MCX Commodity Futures)';

        if (isOptimus) {
            setIsTriggeringOptimus(true);
        } else {
            setIsTriggeringPledge(true);
        }

        const toastId = toast.loading(`Initiating ${title} automated sync pipeline...`);

        try {
            const res = await apiClient.post(`${BACKTEST_API_URL}${endpoint}`);
            toast.success(res.data?.message || `${title} pipeline started successfully!`, { id: toastId });
            
            // Immediately schedule refreshes so new job appears in history
            setTimeout(fetchJobs, 1000);
            setTimeout(fetchJobs, 3000);
            setTimeout(fetchJobs, 6000);
            setTimeout(fetchPipelineStatus, 2000);
        } catch (err) {
            console.error(`Trigger ${type} failed`, err);
            const msg = err.response?.data?.detail || err.message || `Failed to trigger ${title} pipeline`;
            toast.error(msg, { id: toastId });
            setError(msg);
        } finally {
            if (isOptimus) {
                setIsTriggeringOptimus(false);
            } else {
                setIsTriggeringPledge(false);
            }
        }
    };

    const fetchJobs = async () => {
        try {
            setIsRefreshing(true);
            const res = await apiClient.get(`${BACKTEST_API_URL}/jobs?job_type=backtest`);
            const sortedJobs = res.data.filter(j => j.job_type !== 'audit');
            setJobs(sortedJobs);

            if (!jobId && sortedJobs.length > 0) {
                const latest = sortedJobs[0];
                if (['QUEUED', 'UPLOADING', 'UNZIPPING', 'PROCESSING'].includes(latest.status)) {
                    updateState({
                        jobId: latest.id,
                        status: latest.status,
                        progress: latest.progress,
                        stage: latest.message,
                        error: null,
                        lastUpdated: formatTime(latest.updated_at)
                    });
                }
            }
        } catch (err) {
            console.error("Failed to fetch jobs", err);
        } finally {
            setIsRefreshing(false);
        }
    };

    // Auto-resume & status fetch on MOUNT
    useEffect(() => {
        const init = async () => {
            try {
                fetchPipelineStatus();
                const res = await apiClient.get(`${BACKTEST_API_URL}/jobs?job_type=backtest`);
                const sortedJobs = res.data.filter(j => j.job_type !== 'audit');
                setJobs(sortedJobs);

                if (sortedJobs.length > 0) {
                    const latest = sortedJobs[0];
                    updateState({
                        jobId: latest.id,
                        status: latest.status === 'COMPLETED' ? 'DONE' : latest.status,
                        progress: latest.progress,
                        stage: latest.message,
                        error: latest.status === 'FAILED' ? latest.message : null,
                        lastUpdated: formatTime(latest.updated_at)
                    });
                }
            } catch (e) { 
                console.error(e); 
            }
        };
        init();
    }, []);

    // Refresh list when job status changes to DONE or FAILED
    useEffect(() => {
        if (status === 'DONE' || status === 'FAILED') {
            fetchJobs();
            fetchPipelineStatus();
        }
    }, [status]);

    const pollStatus = React.useCallback(async () => {
        if (!jobId) return;
        try {
            const res = await apiClient.get(`${BACKTEST_API_URL}/job/${jobId}`);
            const data = res.data;

            if (data.job_type === 'audit') {
                console.warn("Detected Audit job in Backtest view. Resetting state.");
                setJobId(null);
                setStatus('IDLE');
                resetState();
                return;
            }

            setProgress(data.progress);
            setStage(data.message || data.status);
            setLastUpdated(new Date().toLocaleTimeString());

            if (data.status === 'COMPLETED') {
                setStatus('DONE');
                setProgress(100);
            } else if (data.status === 'FAILED') {
                setStatus('FAILED');
                setError(data.message || 'Backtest failed');
            } else {
                if (data.status === 'QUEUED' || data.status === 'UPLOADING') setStatus('UPLOADING');
                else setStatus('PROCESSING');
            }
        } catch (err) {
            console.error("Poll error", err);
            if (err.response && err.response.status === 404) {
                setJobId(null);
                setStatus('IDLE');
                resetState();
            }
        }
    }, [jobId]);

    const fetchStatus = async () => {
        setIsRefreshing(true);
        await pollStatus();
        setIsRefreshing(false);
    };

    useEffect(() => {
        let interval;
        if ((status === 'PROCESSING' || status === 'UPLOADING') && jobId) {
            pollStatus();
            interval = setInterval(pollStatus, 3000);
        }
        return () => clearInterval(interval);
    }, [status, jobId, pollStatus]);

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            const lowerName = selectedFile.name.toLowerCase();
            const upperName = selectedFile.name.toUpperCase();
            
            const mcxTokens = ['GOLDM', 'SILVERM', 'CRUDEOIL', 'CRUDEOILM', 'NATURALGAS', 'NATGASMINI', 'NATGAS', 'COPPER'];
            const isMcxMatch = mcxTokens.some(t => upperName.includes(t)) || lowerName.endsWith('.csv');

            // Auto-switch mode if user drops an MCX file
            if (isMcxMatch && strategyMode !== 'pledge_plus') {
                setStrategyMode('pledge_plus');
            }

            if (strategyMode === 'optimus' && !lowerName.endsWith('.zip') && !isMcxMatch) {
                setError('Please select a .zip file for Optimus Index Options');
                return;
            }

            if (!lowerName.endsWith('.zip') && !lowerName.endsWith('.csv')) {
                setError('Please select a valid .zip or .csv file');
                return;
            }

            setFile(selectedFile);
            setError(null);
            resetState();
        }
    };

    const handleStartBacktest = async () => {
        if (!file) return;

        try {
            setStatus('UPLOADING');
            setError(null);

            const formData = new FormData();
            formData.append('file', file);
            
            const isPledgePlus = strategyMode === 'pledge_plus';
            if (isPledgePlus) {
                formData.append('market_type', 'mcx_futures');
            } else {
                formData.append('market_type', 'index_options');
            }

            const targetUrl = isPledgePlus 
                ? `${BACKTEST_API_URL}/upload-mcx` 
                : `${BACKTEST_API_URL}/upload`;

            const res = await apiClient.post(targetUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            const { job_id } = res.data;
            setJobId(job_id);

            setStatus('PROCESSING');
            setStage(`Upload Complete. Starting ${isPledgePlus ? 'Pledge+ MCX' : 'Optimus'} Backtest...`);
            fetchStatus();

        } catch (err) {
            console.error(err);
            setStatus('FAILED');
            setError(err.response?.data?.detail || err.message || 'Failed to start backtest');
        }
    };

    const handleCancel = async () => {
        if (!jobId) return;
        try {
            await apiClient.post(`${BACKTEST_API_URL}/job/${jobId}/cancel`);
            setStatus('CANCELLED');
            setError('Result cancelled by user');
        } catch (err) {
            console.error("Cancel failed", err);
        }
    };

    const handleDeleteJob = (id, e) => {
        e.stopPropagation();
        setJobToDelete(id);
    };

    const confirmDelete = async () => {
        if (!jobToDelete) return;

        const id = jobToDelete;
        try {
            await apiClient.delete(`${BACKTEST_API_URL}/job/${id}`);
            if (id === jobId) {
                handleReset();
            }
            fetchJobs();
            setJobToDelete(null);
            toast.success("Job deleted successfully");
        } catch (err) {
            console.error("Delete failed", err);
            const status = err.response?.status;
            const detail = err.response?.data?.detail;
            setError(`Failed to delete job (${status || 'Unknown Error'}): ${detail || err.message}`);
            setJobToDelete(null);
        }
    };

    const handleReset = () => {
        setFile(null);
        resetState();
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDownload = async () => {
        if (!jobId || status !== 'DONE') return;
        try {
            const downloadUrl = `${BACKTEST_API_URL}/job/${jobId}/result`;
            window.location.href = downloadUrl;
        } catch (e) {
            console.error("Download fail", e);
        }
    };

    // Filtered jobs list based on history tab
    const filteredJobs = useMemo(() => {
        if (historyFilter === 'all') return jobs;
        return jobs.filter(j => detectJobCategory(j) === historyFilter);
    }, [jobs, historyFilter]);

    const optimusCount = useMemo(() => jobs.filter(j => detectJobCategory(j) === 'optimus').length, [jobs]);
    const pledgeCount = useMemo(() => jobs.filter(j => detectJobCategory(j) === 'pledge_plus').length, [jobs]);

    return (
        <div className="flex flex-col h-full bg-[#0f1116] text-gray-100 overflow-hidden relative">

            {/* Top Toolbar / Header */}
            <div className="flex flex-wrap items-center justify-between px-6 py-3.5 border-b border-gray-800 bg-[#1a1d29]/90 backdrop-blur shrink-0 gap-4">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-gradient-to-br from-blue-600/30 to-indigo-600/20 border border-blue-500/30 rounded-xl shadow-inner">
                        <Layers className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-lg font-bold tracking-tight text-white">Backtest Manager</h1>
                            <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-bold rounded-full flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                Dual-Engine Live
                            </span>
                        </div>
                        <p className="text-xs text-gray-400 font-medium">Automated & On-Demand Backtest Pipelines for Optimus Index Options & Pledge+ MCX Commodities</p>
                    </div>
                </div>

                {/* Right side controls: Trigger Buttons & Engine Switcher */}
                <div className="flex items-center gap-3 flex-wrap">

                    {/* Quick Trigger Optimus Button */}
                    <button
                        onClick={() => handleTriggerPipeline('optimus')}
                        disabled={isTriggeringOptimus}
                        className="flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-md shadow-blue-900/30 border border-blue-400/30 active:scale-95 disabled:opacity-50"
                        title="Trigger Optimus Index Options Sync (16:30 IST Pipeline)"
                    >
                        {isTriggeringOptimus ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-white" />
                        ) : (
                            <Zap className="w-3.5 h-3.5 text-yellow-300 fill-yellow-300" />
                        )}
                        <span>Trigger Optimus</span>
                    </button>

                    {/* Quick Trigger Pledge+ Button */}
                    <button
                        onClick={() => handleTriggerPipeline('pledge_plus')}
                        disabled={isTriggeringPledge}
                        className="flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white shadow-md shadow-amber-900/30 border border-amber-400/30 active:scale-95 disabled:opacity-50"
                        title="Trigger Pledge+ MCX Commodity Sync (00:30 IST Pipeline)"
                    >
                        {isTriggeringPledge ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-white" />
                        ) : (
                            <Shield className="w-3.5 h-3.5 text-amber-200 fill-amber-200" />
                        )}
                        <span>Trigger Pledge+</span>
                    </button>

                    {/* Strategy Mode Switcher */}
                    <div className="flex items-center gap-1 bg-[#0f1116] p-1 rounded-xl border border-gray-800 shadow-sm ml-1">
                        <button
                            onClick={() => { setStrategyMode('optimus'); handleReset(); }}
                            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                                strategyMode === 'optimus'
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/60'
                            }`}
                        >
                            <Zap className="w-3 h-3 text-yellow-400" />
                            Optimus (Index)
                        </button>
                        <button
                            onClick={() => { setStrategyMode('pledge_plus'); handleReset(); }}
                            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                                strategyMode === 'pledge_plus'
                                    ? 'bg-amber-600 text-white shadow-sm'
                                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/60'
                            }`}
                        >
                            <Shield className="w-3 h-3 text-amber-300" />
                            Pledge+ (MCX)
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 overflow-hidden p-6">
                <div className="grid grid-cols-12 gap-6 h-full">

                    {/* LEFT PANEL: Trigger Actions, Method Switcher, Upload & Execution Status (4 cols) */}
                    <div className="col-span-12 lg:col-span-4 flex flex-col gap-5 h-full overflow-y-auto pr-1 custom-scrollbar">

                        {/* Input Method Switcher Tab (Auto Fyers Sync vs Manual Upload) */}
                        {status === 'IDLE' && (
                            <div className="bg-[#1a1d29] rounded-2xl border border-gray-800 p-1 shadow-xl flex flex-col relative overflow-hidden">
                                
                                {/* Inner Tab Switcher */}
                                <div className="p-2 border-b border-gray-800/80 bg-[#14161f] rounded-t-xl flex items-center justify-between">
                                    <div className="flex items-center gap-1 w-full">
                                        <button
                                            onClick={() => setInputMethod('auto_sync')}
                                            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                                inputMethod === 'auto_sync'
                                                    ? 'bg-gradient-to-r from-blue-600/80 to-indigo-600/80 text-white border border-blue-500/40 shadow-sm'
                                                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/40'
                                            }`}
                                        >
                                            <Sparkles className="w-3.5 h-3.5 text-yellow-400" />
                                            Auto Cloud Sync (API)
                                        </button>

                                        <button
                                            onClick={() => setInputMethod('manual_upload')}
                                            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                                inputMethod === 'manual_upload'
                                                    ? 'bg-gray-800 text-white border border-gray-700 shadow-sm'
                                                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/40'
                                            }`}
                                        >
                                            <UploadCloud className="w-3.5 h-3.5 text-gray-400" />
                                            Manual File Upload
                                        </button>
                                    </div>
                                </div>

                                {/* TAB 1: AUTO CLOUD SYNC & TRIGGER */}
                                {inputMethod === 'auto_sync' && (
                                    <div className="p-5 space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className={`p-2 rounded-lg ${
                                                    strategyMode === 'pledge_plus' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
                                                }`}>
                                                    <Cpu className="w-4 h-4" />
                                                </div>
                                                <div>
                                                    <h3 className="text-xs font-bold text-white uppercase tracking-wider">
                                                        {strategyMode === 'pledge_plus' ? 'Pledge+ MCX Pipeline' : 'Optimus Index Pipeline'}
                                                    </h3>
                                                    <p className="text-[11px] text-gray-400">Automated Fyers 1H Sync Engine</p>
                                                </div>
                                            </div>

                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase ${
                                                strategyMode === 'pledge_plus'
                                                    ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                                    : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                            }`}>
                                                {strategyMode === 'pledge_plus' ? 'MCX Futures' : 'Index Options'}
                                            </span>
                                        </div>

                                        {/* Engine Details Box */}
                                        <div className="bg-[#0f1116] rounded-xl border border-gray-800 p-3.5 space-y-2.5">
                                            <div className="flex items-center justify-between text-[11px]">
                                                <span className="text-gray-400 flex items-center gap-1.5">
                                                    <Clock className="w-3.5 h-3.5 text-gray-500" /> Auto Schedule:
                                                </span>
                                                <span className="font-mono font-semibold text-emerald-400">
                                                    {strategyMode === 'pledge_plus' ? 'Daily 00:30 IST (Tue-Sat)' : 'Daily 16:30 IST (Mon-Fri)'}
                                                </span>
                                            </div>

                                            <div className="flex items-center justify-between text-[11px]">
                                                <span className="text-gray-400 flex items-center gap-1.5">
                                                    <Activity className="w-3.5 h-3.5 text-gray-500" /> Resolution:
                                                </span>
                                                <span className="font-mono text-gray-300">Direct 60m (1-Hour) Native</span>
                                            </div>

                                            <div className="flex items-start justify-between text-[11px] pt-1 border-t border-gray-800/80">
                                                <span className="text-gray-400">Underlyings:</span>
                                                <div className="flex flex-wrap gap-1 justify-end max-w-[200px]">
                                                    {strategyMode === 'pledge_plus' ? (
                                                        ['GOLDM', 'SILVERM', 'CRUDEOIL', 'NATURALGAS', 'COPPER'].map(t => (
                                                            <span key={t} className="px-1.5 py-0.2 bg-amber-500/10 border border-amber-500/20 text-amber-300 text-[9px] font-mono rounded">
                                                                {t}
                                                            </span>
                                                        ))
                                                    ) : (
                                                        ['NIFTY', 'BANKNIFTY', 'SENSEX', 'FINNIFTY', 'MIDCPNIFTY'].map(t => (
                                                            <span key={t} className="px-1.5 py-0.2 bg-blue-500/10 border border-blue-500/20 text-blue-300 text-[9px] font-mono rounded">
                                                                {t}
                                                            </span>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Big Trigger Call Button */}
                                        <button
                                            onClick={() => handleTriggerPipeline(strategyMode)}
                                            disabled={strategyMode === 'optimus' ? isTriggeringOptimus : isTriggeringPledge}
                                            className={`w-full flex items-center justify-center gap-2.5 py-3 rounded-xl font-bold text-sm text-white shadow-xl transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50 ${
                                                strategyMode === 'pledge_plus'
                                                    ? 'bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 hover:from-amber-500 hover:to-orange-500 shadow-amber-900/40 border border-amber-500/30'
                                                    : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-500 hover:to-indigo-500 shadow-blue-900/40 border border-blue-500/30'
                                            }`}
                                        >
                                            {(strategyMode === 'optimus' ? isTriggeringOptimus : isTriggeringPledge) ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    <span>Triggering {strategyMode === 'pledge_plus' ? 'Pledge+' : 'Optimus'} Pipeline...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Play className="w-4 h-4 fill-current" />
                                                    <span>Trigger {strategyMode === 'pledge_plus' ? 'Pledge+ MCX' : 'Optimus Index'} Sync & Run</span>
                                                </>
                                            )}
                                        </button>

                                        <p className="text-[10px] text-gray-500 text-center leading-relaxed">
                                            Fetches OHLCV from Fyers, computes 10 EMA strikes, runs the strategy backtester, and updates the Audit comparison database.
                                        </p>
                                    </div>
                                )}

                                {/* TAB 2: MANUAL FILE UPLOAD */}
                                {inputMethod === 'manual_upload' && (
                                    <div className="p-5 space-y-4">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                                {strategyMode === 'pledge_plus' ? (
                                                    <>
                                                        <Shield className="w-4 h-4 text-amber-400" />
                                                        Pledge+ MCX Upload
                                                    </>
                                                ) : (
                                                    <>
                                                        <Zap className="w-4 h-4 text-blue-400" />
                                                        Optimus Index Upload
                                                    </>
                                                )}
                                            </h3>

                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase ${
                                                strategyMode === 'pledge_plus'
                                                    ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                                    : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                            }`}>
                                                {strategyMode === 'pledge_plus' ? 'MCX Futures' : 'Index Options'}
                                            </span>
                                        </div>

                                        {!file ? (
                                            <div className="border-2 border-dashed border-gray-700 hover:border-blue-500/50 rounded-xl p-6 transition-all duration-300 text-center relative bg-[#0f1116]/50">
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept={strategyMode === 'pledge_plus' ? '.csv,.zip' : '.zip'}
                                                    onChange={handleFileSelect}
                                                    className="hidden"
                                                    id="file-upload"
                                                />
                                                <label
                                                    htmlFor="file-upload"
                                                    className="cursor-pointer flex flex-col items-center gap-2.5"
                                                >
                                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform ${
                                                        strategyMode === 'pledge_plus'
                                                            ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                                            : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                                                    }`}>
                                                        {strategyMode === 'pledge_plus' ? (
                                                            <FileSpreadsheet className="w-6 h-6 text-amber-400" />
                                                        ) : (
                                                            <UploadCloud className="w-6 h-6 text-blue-400" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <span className={`font-semibold hover:underline text-xs ${strategyMode === 'pledge_plus' ? 'text-amber-400' : 'text-blue-400'}`}>
                                                            Click to upload
                                                        </span>
                                                        <span className="text-gray-400 text-xs"> or drag & drop</span>
                                                    </div>
                                                    <p className="text-[10px] text-gray-500 max-w-[220px] leading-relaxed">
                                                        {strategyMode === 'pledge_plus'
                                                            ? 'Single CSV (1-hour OHLC) or ZIP package'
                                                            : 'Index Options package (.zip archive)'
                                                        }
                                                    </p>
                                                </label>
                                            </div>
                                        ) : (
                                            <div className="bg-[#0f1116] rounded-xl border border-gray-700 p-4 relative overflow-hidden">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-center gap-3 overflow-hidden">
                                                        <div className={`p-2.5 rounded-lg shrink-0 ${
                                                            strategyMode === 'pledge_plus' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
                                                        }`}>
                                                            {file.name.endsWith('.csv') ? (
                                                                <FileSpreadsheet className="w-5 h-5" />
                                                            ) : (
                                                                <FileCode className="w-5 h-5" />
                                                            )}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium text-white truncate">{file.name}</p>
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                <span className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                                                                <span className={`px-1.5 py-0.2 rounded text-[9px] font-mono uppercase font-bold ${
                                                                    strategyMode === 'pledge_plus' ? 'bg-amber-500/20 text-amber-300' : 'bg-blue-500/20 text-blue-300'
                                                                }`}>
                                                                    {strategyMode === 'pledge_plus' ? 'PLEDGE+' : 'OPTIMUS'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button onClick={handleReset} className="text-gray-400 hover:text-red-400 hover:bg-red-400/10 p-1.5 rounded-lg transition-colors cursor-pointer">
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>

                                                <button
                                                    onClick={handleStartBacktest}
                                                    className={`mt-4 w-full flex items-center justify-center gap-2 text-white py-2.5 rounded-lg font-semibold shadow-lg transition-all active:scale-[0.98] cursor-pointer ${
                                                        strategyMode === 'pledge_plus'
                                                            ? 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 shadow-amber-900/30'
                                                            : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-blue-900/30'
                                                    }`}
                                                >
                                                    <Play className="w-4 h-4 fill-current" />
                                                    Initialize {strategyMode === 'pledge_plus' ? 'Pledge+ MCX' : 'Optimus'} Backtest
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Active Job Execution Status (When Running, Done, or Failed) */}
                        {status !== 'IDLE' && (
                            <div className="bg-[#1a1d29] rounded-2xl border border-gray-800 p-1 shadow-xl flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-500">
                                <div className="p-5 space-y-5">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                            <Loader2 className={`w-4 h-4 ${status === 'PROCESSING' || status === 'UPLOADING' ? 'animate-spin text-blue-400' : ''}`} />
                                            Current Execution
                                        </h3>
                                        {status === 'DONE' && <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded border border-emerald-500/20">COMPLETED</span>}
                                        {status === 'FAILED' && <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold rounded border border-red-500/20">FAILED</span>}
                                        {status === 'CANCELLED' && <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded border border-yellow-500/20">CANCELLED</span>}
                                    </div>

                                    {/* Progress Radial or Bar */}
                                    <div className="flex flex-col items-center py-2">
                                        {status === 'DONE' ? (
                                            <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center border-4 border-emerald-500/20 mb-3 relative">
                                                <div className="absolute inset-0 rounded-full border-4 border-emerald-500 opacity-20 animate-ping"></div>
                                                <CheckCircle className="w-8 h-8 text-emerald-500" />
                                            </div>
                                        ) : (status === 'FAILED' || status === 'CANCELLED') ? (
                                            <div className="flex flex-col items-center w-full">
                                                <div className={`w-20 h-20 rounded-full flex items-center justify-center border-4 mb-3 ${status === 'FAILED' ? 'bg-red-500/10 border-red-500/20' : 'bg-yellow-500/10 border-yellow-500/20'}`}>
                                                    <X className={`w-8 h-8 ${status === 'FAILED' ? 'text-red-500' : 'text-yellow-500'}`} />
                                                </div>

                                                <div className="text-center mt-2">
                                                    <button
                                                        onClick={handleReset}
                                                        className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors border border-gray-700 hover:border-gray-600 cursor-pointer"
                                                    >
                                                        Start New Backtest
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="w-full space-y-4">
                                                <div className="flex justify-between text-xs font-medium text-gray-400">
                                                    <span>Progress</span>
                                                    <span className="text-blue-400 font-bold">{progress}%</span>
                                                </div>
                                                <div className="h-2.5 bg-gray-800 rounded-full overflow-hidden border border-gray-700/50">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-blue-600 via-indigo-500 to-emerald-500 transition-all duration-500 relative"
                                                        style={{ width: `${progress}%` }}
                                                    >
                                                        <div className="absolute inset-0 bg-white/20 animate-[shimmer_2s_infinite]"></div>
                                                    </div>
                                                </div>
                                                <div className="text-center flex justify-center gap-4 mt-2">
                                                    <button
                                                        onClick={handleCancel}
                                                        className="text-xs font-semibold text-red-500 hover:text-red-400 transition-colors uppercase tracking-wide border border-red-500/30 px-3 py-1 rounded hover:bg-red-500/10 cursor-pointer"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleReset}
                                                        className="text-xs text-gray-400 hover:text-white transition-colors underline cursor-pointer"
                                                    >
                                                        Start New
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        <p className="mt-4 text-center text-sm font-medium text-white px-2">
                                            {stage || error || "Initializing..."}
                                        </p>
                                        {lastUpdated && <p className="text-[10px] text-gray-500 mt-1">Updated: {lastUpdated}</p>}
                                    </div>

                                    {status === 'DONE' && (
                                        <div className="grid grid-cols-2 gap-3 pt-2">
                                            <button
                                                onClick={handleDownload}
                                                className="col-span-2 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white py-2.5 rounded-lg font-semibold transition-colors shadow-lg shadow-emerald-900/20 cursor-pointer"
                                            >
                                                <Download className="w-4 h-4" /> Download Result
                                            </button>
                                            <button
                                                onClick={handleReset}
                                                className="col-span-2 text-xs text-gray-400 hover:text-gray-200 py-2 underline decoration-gray-700 hover:decoration-gray-500 cursor-pointer"
                                            >
                                                Start New Backtest
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Pipeline Status Summary Card */}
                        <div className="bg-[#1a1d29] rounded-2xl border border-gray-800 p-4 shadow-xl">
                            <div className="flex items-center justify-between mb-2.5">
                                <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-blue-400" />
                                    <span className="text-xs font-bold text-gray-300 uppercase tracking-wider">Automated Schedules</span>
                                </div>
                                <button
                                    onClick={fetchPipelineStatus}
                                    className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                                    title="Refresh status"
                                >
                                    <RefreshCcw className="w-3.5 h-3.5" />
                                </button>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between p-2 rounded-lg bg-[#0f1116] border border-gray-800/80">
                                    <div className="flex items-center gap-2">
                                        <Zap className="w-3.5 h-3.5 text-yellow-400" />
                                        <div>
                                            <p className="text-xs font-semibold text-white">Optimus Index Options</p>
                                            <p className="text-[10px] text-gray-400">16:30 IST (Mon–Fri)</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleTriggerPipeline('optimus')}
                                        disabled={isTriggeringOptimus}
                                        className="px-2.5 py-1 bg-blue-600/20 hover:bg-blue-600 text-blue-300 hover:text-white rounded text-[10px] font-bold border border-blue-500/30 transition-all cursor-pointer disabled:opacity-50"
                                    >
                                        {isTriggeringOptimus ? <Loader2 className="w-3 h-3 animate-spin" /> : "Trigger"}
                                    </button>
                                </div>

                                <div className="flex items-center justify-between p-2 rounded-lg bg-[#0f1116] border border-gray-800/80">
                                    <div className="flex items-center gap-2">
                                        <Shield className="w-3.5 h-3.5 text-amber-400" />
                                        <div>
                                            <p className="text-xs font-semibold text-white">Pledge+ MCX Futures</p>
                                            <p className="text-[10px] text-gray-400">00:30 IST (Tue–Sat)</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleTriggerPipeline('pledge_plus')}
                                        disabled={isTriggeringPledge}
                                        className="px-2.5 py-1 bg-amber-600/20 hover:bg-amber-600 text-amber-300 hover:text-white rounded text-[10px] font-bold border border-amber-500/30 transition-all cursor-pointer disabled:opacity-50"
                                    >
                                        {isTriggeringPledge ? <Loader2 className="w-3 h-3 animate-spin" /> : "Trigger"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && status !== 'FAILED' && (
                            <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-xl flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="text-red-400 font-medium text-sm">System Error</h4>
                                    <p className="text-red-300/80 text-xs mt-1 leading-relaxed">{error}</p>
                                </div>
                            </div>
                        )}

                    </div>

                    {/* RIGHT PANEL: History Table (8 cols) */}
                    <div className="col-span-12 lg:col-span-8 h-full bg-[#1a1d29] rounded-2xl border border-gray-800 shadow-xl flex flex-col overflow-hidden">
                        
                        {/* History Header with Tabs */}
                        <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-[#1a1d29] shrink-0">
                            <div className="flex items-center gap-4">
                                <h3 className="text-xs font-bold text-gray-200 uppercase tracking-wider">Backtest Execution History</h3>
                                
                                {/* Filter Tabs */}
                                <div className="flex items-center gap-1 bg-[#0f1116] p-0.5 rounded-lg border border-gray-800">
                                    <button
                                        onClick={() => setHistoryFilter('all')}
                                        className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${
                                            historyFilter === 'all'
                                                ? 'bg-gray-800 text-white'
                                                : 'text-gray-400 hover:text-gray-200'
                                        }`}
                                    >
                                        All ({jobs.length})
                                    </button>
                                    <button
                                        onClick={() => setHistoryFilter('optimus')}
                                        className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${
                                            historyFilter === 'optimus'
                                                ? 'bg-blue-600/30 text-blue-300 border border-blue-500/30'
                                                : 'text-gray-400 hover:text-gray-200'
                                        }`}
                                    >
                                        <Zap className="w-3 h-3 text-yellow-400" />
                                        Optimus ({optimusCount})
                                    </button>
                                    <button
                                        onClick={() => setHistoryFilter('pledge_plus')}
                                        className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${
                                            historyFilter === 'pledge_plus'
                                                ? 'bg-amber-600/30 text-amber-300 border border-amber-500/30'
                                                : 'text-gray-400 hover:text-gray-200'
                                        }`}
                                    >
                                        <Shield className="w-3 h-3 text-amber-400" />
                                        Pledge+ ({pledgeCount})
                                    </button>
                                </div>
                            </div>

                            <button
                                onClick={fetchJobs}
                                className={`p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-all cursor-pointer ${isRefreshing ? 'animate-spin text-blue-400' : ''}`}
                                title="Refresh History"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto custom-scrollbar p-0">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-[#14161f] text-xs uppercase text-gray-400 font-semibold sticky top-0 z-10 shadow-sm">
                                    <tr>
                                        <th className="px-6 py-3 border-b border-gray-800">Timestamp</th>
                                        <th className="px-6 py-3 border-b border-gray-800">Engine / Type</th>
                                        <th className="px-6 py-3 border-b border-gray-800">Strategy / Trigger</th>
                                        <th className="px-6 py-3 border-b border-gray-800">Status</th>
                                        <th className="px-6 py-3 border-b border-gray-800 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-800/50">
                                    {filteredJobs.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="text-center py-20">
                                                <div className="flex flex-col items-center gap-3 opacity-50">
                                                    <FileCode className="w-10 h-10 text-gray-600" />
                                                    <span className="text-gray-500 text-sm">
                                                        {historyFilter === 'all' 
                                                            ? 'No history available' 
                                                            : `No ${historyFilter === 'pledge_plus' ? 'Pledge+' : 'Optimus'} backtests found`
                                                        }
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredJobs.map((job) => {
                                            const category = detectJobCategory(job);
                                            const isMCX = category === 'pledge_plus';

                                            return (
                                                <tr key={job.id} className="hover:bg-[#202330] transition-colors group">
                                                    <td className="px-6 py-4 text-xs text-gray-400 whitespace-nowrap font-mono">
                                                        {formatDateTime(job.created_at)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {isMCX ? (
                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase bg-amber-500/10 text-amber-300 border border-amber-500/20">
                                                                <Shield className="w-3 h-3 text-amber-400" />
                                                                PLEDGE+
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase bg-blue-500/10 text-blue-300 border border-blue-500/20">
                                                                <Zap className="w-3 h-3 text-yellow-400" />
                                                                OPTIMUS
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-white font-medium">
                                                        <div className="flex items-center gap-2">
                                                            {job.original_filename?.endsWith('.csv') ? (
                                                                <FileSpreadsheet className="w-4 h-4 text-amber-400" />
                                                            ) : (
                                                                <FileCode className="w-4 h-4 text-blue-400" />
                                                            )}
                                                            <span className="font-mono text-xs">{job.original_filename || "Automated Sync Job"}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase shadow-sm
                                                            ${job.status === 'COMPLETED' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                                                                job.status === 'FAILED' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                                                                    'bg-blue-500/10 text-blue-400 border border-blue-500/20 animate-pulse'}`}>
                                                            <span className={`w-1.5 h-1.5 rounded-full ${job.status === 'COMPLETED' ? 'bg-emerald-400' :
                                                                job.status === 'FAILED' ? 'bg-red-400' : 'bg-blue-400'
                                                                }`}></span>
                                                            {job.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="flex justify-end gap-2 transition-opacity">
                                                            {(job.status === 'UPLOADING' || job.status === 'PROCESSING' || job.status === 'QUEUED') ? (
                                                                <button
                                                                    onClick={() => {
                                                                        setJobId(job.id);
                                                                        setStatus('PROCESSING');
                                                                    }}
                                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/10 text-blue-400 hover:bg-blue-600 hover:text-white rounded-md text-xs font-medium transition-all cursor-pointer"
                                                                >
                                                                    <Loader2 className="w-3 h-3 animate-spin" /> Monitor
                                                                </button>
                                                            ) : (
                                                                <>
                                                                    {job.status === 'COMPLETED' && (
                                                                        <a
                                                                            href={`${BACKTEST_API_URL}/job/${job.id}/result`}
                                                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600/10 text-emerald-400 hover:bg-emerald-600 hover:text-white rounded-md text-xs font-medium transition-all cursor-pointer"
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            <Download className="w-3 h-3" /> Report
                                                                        </a>
                                                                    )}
                                                                    <button
                                                                        onClick={(e) => handleDeleteJob(job.id, e)}
                                                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/10 text-red-400 hover:bg-red-600 hover:text-white rounded-md text-xs font-medium transition-all cursor-pointer"
                                                                        title="Delete Job"
                                                                    >
                                                                        <Trash2 className="w-3 h-3" />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            </div>
            
            {/* Delete Confirmation Modal */}
            {jobToDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div
                        className="bg-[#1a1d29] border border-gray-800 rounded-xl p-6 w-full max-w-sm shadow-2xl transform transition-all scale-100"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col items-center text-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-2">
                                <AlertCircle className="w-6 h-6 text-red-500" />
                            </div>

                            <div>
                                <h3 className="text-lg font-bold text-white mb-1">Delete Backtest?</h3>
                                <p className="text-sm text-gray-400">
                                    Are you sure you want to delete this job? This action cannot be undone.
                                </p>
                            </div>

                            <div className="flex gap-3 w-full mt-2">
                                <button
                                    onClick={() => setJobToDelete(null)}
                                    className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm font-medium transition-colors cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors shadow-lg shadow-red-900/20 cursor-pointer"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BacktestView;
